import { Badge, Button, Card, CardContent, Input, Label, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Plus, Trash2, X } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { PageHeader } from '../../components/page-header'
import {
  type CustomField,
  type CustomFieldInput,
  type CustomFieldType,
  createCustomField,
  deleteCustomField,
  listCustomFields,
} from '../../lib/directory-api'

const DATA_TYPES: { value: CustomFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'select', label: 'Single select' },
  { value: 'multiselect', label: 'Multi select' },
]

const typeVariant: Record<string, 'primary' | 'secondary' | 'gold' | 'neutral'> = {
  text: 'neutral',
  number: 'secondary',
  date: 'secondary',
  boolean: 'gold',
  select: 'primary',
  multiselect: 'primary',
}

const inputClass =
  'flex h-10 w-full rounded-md border border-border bg-bg-alt px-3 py-2 text-sm text-fg ' +
  'focus-visible:outline-none focus-visible:border-secondary focus-visible:ring-[3px] focus-visible:ring-ring'

export function CustomFieldsAdminPage() {
  const qc = useQueryClient()
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['custom-fields', 'person'],
    queryFn: () => listCustomFields('person'),
  })

  const [showForm, setShowForm] = useState(false)
  const [label, setLabel] = useState('')
  const [key, setKey] = useState('')
  const [dataType, setDataType] = useState<CustomFieldType>('text')
  const [optionsText, setOptionsText] = useState('')
  const [isRequired, setIsRequired] = useState(false)
  const [isSearchable, setIsSearchable] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const resetForm = () => {
    setLabel('')
    setKey('')
    setDataType('text')
    setOptionsText('')
    setIsRequired(false)
    setIsSearchable(false)
    setFormError(null)
    setShowForm(false)
  }

  const createMut = useMutation({
    mutationFn: (input: CustomFieldInput) => createCustomField(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['custom-fields', 'person'] })
      resetForm()
    },
    onError: (e) => setFormError(e instanceof Error ? e.message : 'Could not create field'),
  })

  const deleteMut = useMutation({
    mutationFn: (fieldId: string) => deleteCustomField(fieldId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['custom-fields', 'person'] }),
  })

  const needsOptions = dataType === 'select' || dataType === 'multiselect'

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setFormError(null)
    const trimmedKey = key.trim()
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(trimmedKey)) {
      setFormError(
        'Key must start with a letter and contain only letters, numbers, or underscores.',
      )
      return
    }
    const options = needsOptions
      ? optionsText
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => ({ value: line, label: line }))
      : undefined
    if (needsOptions && (!options || options.length === 0)) {
      setFormError('Select fields need at least one option (one per line).')
      return
    }
    createMut.mutate({
      key: trimmedKey,
      label: label.trim(),
      dataType,
      options,
      isRequired,
      isSearchable,
    })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Custom fields"
        description="Define extra profile fields for people in your org."
        actions={
          !showForm && (
            <Button onClick={() => setShowForm(true)}>
              <Plus />
              New field
            </Button>
          )
        }
      />

      {showForm && (
        <Card>
          <CardContent className="pt-5">
            <form className="space-y-4" onSubmit={submit}>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="cf-label">Label</Label>
                  <Input
                    id="cf-label"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="Baptism date"
                    required
                    maxLength={120}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cf-key">Key</Label>
                  <Input
                    id="cf-key"
                    value={key}
                    onChange={(e) => setKey(e.target.value)}
                    placeholder="baptism_date"
                    required
                    maxLength={60}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="cf-type">Type</Label>
                <select
                  id="cf-type"
                  className={inputClass}
                  value={dataType}
                  onChange={(e) => setDataType(e.target.value as CustomFieldType)}
                >
                  {DATA_TYPES.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>

              {needsOptions && (
                <div className="space-y-1.5">
                  <Label htmlFor="cf-options">Options (one per line)</Label>
                  <textarea
                    id="cf-options"
                    className={`${inputClass} h-28 resize-y`}
                    value={optionsText}
                    onChange={(e) => setOptionsText(e.target.value)}
                    placeholder={'Option A\nOption B\nOption C'}
                  />
                </div>
              )}

              <div className="flex flex-wrap gap-5">
                <label className="flex items-center gap-2 text-sm text-fg">
                  <input
                    type="checkbox"
                    className="size-4 accent-[var(--secondary)]"
                    checked={isRequired}
                    onChange={(e) => setIsRequired(e.target.checked)}
                  />
                  Required
                </label>
                <label className="flex items-center gap-2 text-sm text-fg">
                  <input
                    type="checkbox"
                    className="size-4 accent-[var(--secondary)]"
                    checked={isSearchable}
                    onChange={(e) => setIsSearchable(e.target.checked)}
                  />
                  Searchable
                </label>
              </div>

              {formError && <p className="text-sm text-danger">{formError}</p>}

              <div className="flex items-center gap-2">
                <Button type="submit" disabled={createMut.isPending}>
                  {createMut.isPending ? <Spinner /> : <Check />}
                  Create field
                </Button>
                <Button type="button" variant="ghost" onClick={resetForm}>
                  <X />
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading fields…
          </div>
        ) : isError ? (
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn’t load custom fields.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'You may not have access.'}
            </p>
          </div>
        ) : !data || data.length === 0 ? (
          <div className="py-20 text-center text-sm text-muted">
            No custom fields yet. Create one to capture extra data on profiles.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                  <th className="px-5 py-3 font-semibold">Label</th>
                  <th className="px-5 py-3 font-semibold">Key</th>
                  <th className="px-5 py-3 font-semibold">Type</th>
                  <th className="px-5 py-3 font-semibold">Flags</th>
                  <th className="px-5 py-3 font-semibold sr-only">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((f: CustomField) => (
                  <tr
                    key={f.id}
                    className="border-b border-border-soft transition-colors last:border-0 hover:bg-card-hover"
                  >
                    <td className="px-5 py-3 font-medium">{f.label}</td>
                    <td className="px-5 py-3 font-mono text-xs text-muted">{f.key}</td>
                    <td className="px-5 py-3">
                      <Badge variant={typeVariant[f.dataType] ?? 'neutral'} className="capitalize">
                        {f.dataType}
                      </Badge>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {f.isRequired && <Badge variant="warning">Required</Badge>}
                        {f.isSearchable && <Badge variant="secondary">Searchable</Badge>}
                        {!f.isRequired && !f.isSearchable && <span className="text-dim">—</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Delete ${f.label}`}
                        disabled={deleteMut.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Delete custom field “${f.label}”? Existing values will be removed.`,
                            )
                          ) {
                            deleteMut.mutate(f.id)
                          }
                        }}
                      >
                        <Trash2 className="text-danger" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
